
import { useContext, useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { Alert } from '@mui/lab';
import { gql } from '@apollo/client';

import client from '@/client'

import moment from 'moment';

import { VBox } from '@/components/VBox';
import { HBox } from '@/components/HBox';

import i18n from '@/i18n';

import { TimeSeries } from '@/components/TimeSeries';

import { Box, Container, Button, Select, MenuItem, Switch, Table, TableHead, TableRow, TableCell, TableBody, FormControl, TextField, Checkbox } from '@mui/material';

import { Autocomplete } from '@mui/lab';

import { CloudDownload } from '@mui/icons-material';
import { downloadStatistics } from '@/actions/console';
import { LoadingPage } from '@/components/LoadingPage';
import { ErrorView } from '@/components/ErrorView';
//import { GenericList } from '@/components/GenericList';
import { Typography } from '@mui/material';
import { VictoryPie } from 'victory';
import { Case, SwitchCase } from '@/components/Syntax';
import { useNavigate } from 'react-router-dom';
import { DiscoveryModeTrendsContainer } from '../discovery-mode/overview';

import { Forward } from '@mui/icons-material';
import { useSearchParam } from '@/hooks';
import { StatisticsContext } from '@/contexts/StatisticsContext';
import { Link } from 'react-router-dom';
import { SpotifyLogo } from 'phosphor-react';

const TOP_LIST_QUERY = gql`
  query getTopList(
    $start: Date,
    $end: Date
  ) {
    leads {
      topList(
        start: $start,
        end: $end,
      ) {
        assets(limit: 10) {
          name,
          artist,
          totals {
            percentage,
            count
          }
        }
      }
    }
  }
`

const QUERY = gql`
  query getStatistics(
    $start: Date,
    $end: Date,
    $labelId: ID,
    $artistIds: [ID],
    $releaseIds: [ID],
    $recordingIds: [ID],
    $organizationIds: [ID],
    $labelIds: [ID],
    $serviceIds: [ID],
    $external: Boolean,
    $organizationId: ID
  ) {
    leads {
      statistics(
        external: $external,
        start: $start,
        end: $end,
        labelId: $labelId,
        artistIds: $artistIds,
        releaseIds: $releaseIds,
        recordingIds: $recordingIds,
        serviceIds: $serviceIds,
        labelIds: $labelIds,
        organizationIds: $organizationIds,
        organizationId: $organizationId
      )
    }
  }
`

export function TopListContainer({
  start,
  end
}) {
  const [topListViewType, setTopListViewType] = useState('list')

  if (!end) {
    end = moment().subtract(2, 'days')
  }

  if (!start) {
    start = moment().subtract(7, 'days')
  }

  const { data, loading, previousData } = useQuery(
    TOP_LIST_QUERY,
    {
      variables: {
        start: moment(start).format('YYYY-MM-DD'),
        end: moment(end).format('YYYY-MM-DD'),
        limit: 20
      }
    }
  )

  const assets = (data ?? previousData)?.leads?.topList?.assets

  if (!assets) {
    return (
      <ErrorView />
    )
  }

  return (
    <VBox>
      <HBox>
        <Typography variant="h4" sx={{ flex: 1 }}>{i18n.t('top-tracks-on-spotify-across-all-labels')}</Typography>
        <Switch
          checked={topListViewType === "pie"}
          onClick={() => {
            setTopListViewType(topListViewType === "list" ? 'pie' : "list")
          }}
        />
      </HBox>
      <SwitchCase value={topListViewType}>
        <Case match="list">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{i18n.t('track')}</TableCell>
                <TableCell>{i18n.t('artist')}</TableCell>
                <TableCell>{i18n.t('percentage-change')}</TableCell>
                <TableCell>{i18n.t('streams')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets?.map((asset, i) => (
                <TableRow key={asset.id}>
                  <TableCell sx={{ textAlign: 'right' }}>{i + 1}</TableCell>
                  <TableCell>{asset.name}<a target="_blank" href={`https://open.spotify.com/search/${encodeURIComponent("\"" + asset.name + "\" \"" + asset.artist + '"')}`}><SpotifyLogo /></a></TableCell>
                  <TableCell>{asset.artist}<a target="_blank" href={`https://open.spotify.com/search/${encodeURIComponent("artist:\"" + asset.artist + '"')}`}><SpotifyLogo /></a></TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{asset.totals.percentage?.toLocaleString(undefined, { minimumFractionDigits: 2, maximunFractionDigits: 2 })} %</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{asset.totals.count?.toLocaleString(undefined, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}</TableCell>
            
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Case>
        <Case match="pie">
          {loading ? (
            <LoadingPage />
          ) : (assets instanceof Array ? (
            <VictoryPie
              colorScale={'qualitative'}
              radius={100}
              style={{
                labels: {
                  fontSize: 5
                }
              }}
              data={
                assets?.map(
                  r => ({
                    x: r.name,
                    y: parseInt(r.totals.count)
                  })
                )
              }
            />
          ) : <ErrorView />)}
        </Case>
      </SwitchCase>
    </VBox>
  )
}

export function StatisticsContainer({
  start,
  end,
  dataKey = 'streams',
  recordingIds,
  serviceIds,
  period,
  artistIds,
  organizationId,
  organizationIds,
  labelIds,
  releaseIds,
  external
}) {
  if (!end) {
    end = moment().subtract(2, 'days')
  }
  if (!start) {
    start = moment().subtract(7, 'days')
  } 
  const { data, loading, error, previousData } = useQuery(
    QUERY,
    {
      variables: {
        start: moment(start)?.format('YYYY-MM-DD'),
        end: moment(end)?.format('YYYY-MM-DD'),
        recordingIds,
        serviceIds,
        external,
        artistIds,
        labelIds,
        releaseIds,
        organizationId: organizationId ?? organizationIds?.[0]
      }
    }
  )

  const statistics = JSON.parse((data ?? previousData)?.leads?.statistics ?? "null")
  return (
    <VBox>
      <Typography variant="h4">{i18n.t('spotify-streaming-statistics-for-all-labels', { period: i18n.t(period), streams: statistics?.stats?.map(s => s.streams).reduce((a, b) => a + b, 0)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) })}</Typography>
      <Typography>{i18n.t('daily-breakdown-in-chart-below')}</Typography>
      <HBox>
        {error && (
          <Alert severity="warning">{i18n.t('missing-login-credentials')}</Alert>
        )}
        {loading ?
          <LoadingPage />
         : (data && statistics) ? (
          <TimeSeries
            key={dataKey}
            dataKey={dataKey}
            start={start}
            end={end}
            data={statistics}
          />
        ) : <ErrorView />}
      </HBox>
    </VBox>
  )
}

const PAGE_QUERY = gql`
  query getPage(
    $organizationId: ID,
    $organizationQuery: String,
    $labelQuery: String
  ) {
    studio {
      organizations(q: $organizationQuery) {
        id
        name
        slug
      }
      organization(organizationId: $organizationId) {
        id
        name
        slug
        labels(q: $labelQuery) {
          id
          slug
          name
        }
      }
    }
  }
`

export default function StatisticsPage() {

  const defaultDataKey = 'streams'
  const [dataKey, setDataKey] = useSearchParam('dataKey', defaultDataKey)

  const {
    start,
    end,
    organizations,
    selectedOrganizations,
    setSelectedOrganizations,
    setOrganizations,
    labels,
    setLabels,
    selectedLabels,
    setSelectedLabels,
    recordings,
    selectedRecordings,
    setSelectedRecordings,
    artists,
    setArtists,
    selectedArtists,
    setSelectedArtists,
    releases,
    selectedReleases,
    setSelectedReleases,
    setStart,
    setEnd
  } = useContext(StatisticsContext)

  const labelIds = selectedLabels?.map(l => l.slug) ?? []
  const organizationIds = selectedOrganizations?.map(o => o.slug) ?? []
  const artistIds = selectedArtists?.map(a => a.slug) ?? []
  const releaseIds = selectedReleases?.map(r => r.slug) ?? []
  const recordingIds = selectedRecordings?.map(r => r.slug) ?? []

  const navigate = useNavigate()

  const period = useMemo(() => {
    let ret = "custom"
    if (
      moment(end).format('YYYY-MM-DD') === moment().subtract(2, 'day').format('YYYY-MM-DD') &&
      moment(start).format('YYYY-MM-DD') === moment().subtract(8, 'day').format('YYYY-MM-DD')
    ) {
      ret = "7-days"
    }
    if (
      moment(end).format('YYYY-MM-DD') === moment().subtract(2, 'day').format('YYYY-MM-DD') &&
      moment(start).format('YYYY-MM-DD') === moment().subtract(29, 'day').format('YYYY-MM-DD')
    ) {
      ret = "28-days"
    }
    return ret
  }, [start, end])

  const setPeriod = (value) => {
    switch (value) {
      case "7-days":
        setEnd(moment().subtract(2, 'day').format('YYYY-MM-DD'))
        setStart(moment().subtract(8, 'day').format('YYYY-MM-DD'))
        break;
      case "28-days":
        setEnd(moment().subtract(2, 'day').format('YYYY-MM-DD'))
        setStart(moment().subtract(29, 'day').format('YYYY-MM-DD'))
        break;
    }
  }

  const handleDownloadStatsClick = event => {
    downloadStatistics({
      query: {
        start,
        end,
        recordingIds,
        serviceIds,
        organizationIds,
        labelIds,
        artistIds,
        releaseIds
      }
    })
  }

  return (
    <Container>
      <VBox>
        <HBox>
          <Typography>{moment(start).format('YYYY-MM-DD')} - {moment(end).format('YYYY-MM-DD')}</Typography>
          <Select
            sx={{ transitionDelay: `300ms`, minWidth: 200 }}
            placeholder={i18n.t('period')}
            defaultValue={period}
            onChange={(event) => {
              setPeriod(event.target.value)
            }}
          >
            <MenuItem value="7-days">{i18n.t('7-days')}</MenuItem>
            <MenuItem value="28-days">{i18n.t('28-days')}</MenuItem>
          </Select>
          <Button
            startDecorator={<CloudDownload />}
            variant="contained"
            sx={{ display: 'none' }}
            onClick={handleDownloadStatsClick}
          >
            {i18n.t('download-stats')}
          </Button>
        </HBox>
        <StatisticsContainer
          period={period}
          serviceIds={[746109]}
          organizationIds={organizationIds}
          labelIds={labelIds}
          artistIds={artistIds}
          releaseIds={releaseIds}
          recordingIds={recordingIds}
          start={start}
          end={end}
        />
        <HBox>
          <VBox>
            <Typography variant="h4" sx={{ flex: 1 }}>{i18n.t('discovery-mode')}</Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/dashboard/discovery-mode/campaigns`)}
              endIcon={<Forward />}
            >
              {i18n.t('view-discovery-mode-campaigns')}
            </Button>
          </VBox>
        </HBox>
        <Container maxWidth="xl">
          <Box sx={{ padding: 1, display: 'flex', flexDirection: "row", width: "100%", alignItems: "center", gap: 1 }}>
            <Typography>{i18n.t('filter-by')}</Typography>
            <FormControl sx={{ minWidth: 220 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterOptions={n => n}
                autoComplete
                options={organizations ?? []}
                defaultValue={selectedOrganizations ?? []}
                getOptionLabel={(option) => option.name}
                onInputChange={(event, newInputValue) => {
                  client.query({
                    query: gql`
                      query getOrganizations(
                        $q: String
                      ) {
                        studio { 
                          organizations(q: $q) {
                            name
                            id
                            slug
                          }
                        }  
                      }
                    `,
                    variables: {
                      q: newInputValue
                    }
                  }).then(({ data }) => {
                    setOrganizations(data.studio.organizations?.slice(0, 10))
                  })
                }}
                onChange={(event, values) => {
                  setSelectedOrganizations(values)
                }}
                renderOption={(props, s, { selected }) => (
                  <li
                    {...props}
                  >
                    <Checkbox sx={{ pointerEvents: 'none' }} checked={selected} />
                    {i18n.t(s.name)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={i18n.t('organization')} />
                )}
              />
            </FormControl>
            <FormControl sx={{ minWidth: 220 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterOptions={n => n}
                options={labels ?? []}
                defaultValue={selectedLabels ?? []}
                getOptionLabel={(option) => option.name}
                onInputChange={(event, newInputValue) => {
                  if (selectedOrganizations?.length > 0) {
                    client.query({
                      query: gql`
                        query getLabels(
                          $organizationId: ID!,
                          $q: String
                        ) {
                          studio { 
                            organization(organizationId: $organizationId) {
                              labels(q: $q) {
                                name
                                id
                                slug
                              }
                            }
                          }  
                        }
                      `,
                      variables: {
                        q: newInputValue,
                        organizationId: selectedOrganizations[0].slug
                      }
                    }).then(({ data }) => {
                      setLabels(data.studio.organization.labels.slice(0, 100))
                    })
                  } else {
                    client.query({
                      query: gql`
                        query getLabels(
                          $q: String
                        ) {
                          studio { 
                            labels(q: $q) {
                              name
                              id
                              slug
                            }
                          }  
                        }
                      `,
                      variables: {
                        q: newInputValue,
                        organizationId: selectedOrganizations?.[0]?.slug
                      }
                    }).then(({ data }) => {
                      setLabels(data.studio.labels.slice(0, 100))
                    })
                  }
                }}
                onChange={(event, values) => {  
                  setSelectedLabels(values)
                }}
                renderOption={(props, s, { selected }) => (
                  <li
                    {...props}
                  >
                    <Checkbox sx={{ pointerEvents: 'none' }} checked={selected} />
                    {i18n.t(s.name)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={i18n.t('label')} />
                )}
              />
            </FormControl>
            <FormControl sx={{ display: 'none', minWidth: 220 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                filterOptions={n => n}
                options={artists ?? []}
                defaultValue={selectedArtists ?? []}
                getOptionLabel={(option) => option.name}
                onInputChange={(event, newInputValue) => {
                  if (selectedOrganizations?.length > 0) {
                    client.query({
                      query: gql`
                        query getArtists(
                          $organizationId: ID!,
                          $q: String
                        ) {
                          studio { 
                            organization(organizationId: $organizationId) {
                              artists(q: $q) {
                                name
                                id
                                slug
                              }
                            }
                          }  
                        }
                      `,
                      variables: {
                        q: newInputValue,
                        organizationId: selectedOrganizations[0].slug
                      }
                    }).then(({ data }) => {
                      setArtists(data.studio.organization.artists.slice(0, 100))
                    })
                  } else {
                    client.query({
                      query: gql`
                        query getArtists(
                          $q: String
                        ) {
                          studio { 
                            artists(q: $q) {
                              name
                              id
                              slug
                            }
                          }  
                        }
                      `,
                      variables: {
                        q: newInputValue,
                        organizationId: selectedOrganizations?.[0]?.slug
                      }
                    }).then(({ data }) => {
                      setArtists(data.studio.artists.slice(0, 100))
                    })
                  }
                }}
                onChange={(event, values) => {
                  setSelectedArtists(values)
                }}
                renderOption={(props, s, { selected }) => (
                  <li
                    {...props}
                  >
                    <Checkbox sx={{ pointerEvents: 'none' }} checked={selected} />
                    {i18n.t(s.name)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={i18n.t('artist')} />
                )}
              />
            </FormControl>
          </Box>
        </Container>
        <DiscoveryModeTrendsContainer
          organizations={selectedOrganizations}
          labels={selectedLabels}
          start={start}
          end={end}
        />
        <TopListContainer
          organizationIds={organizationIds}
          labelIds={labelIds}
          start={start}
          end={end}
        />
      </VBox>
    </Container>
  )
}
